/**
 * Un produit est le regroupement des photos qui le montrent. Il n'en existe
 * aucune entité : un produit n'est qu'un identifiant `AAAA-MM-JJ/HHMMSS` que
 * porte chaque photo, et qui est aussi son chemin dans le stockage distant.
 * Un produit sans photo n'existe donc pas par construction — il naît de sa
 * première — et rien n'a de cycle de vie à tenir cohérent entre deux magasins.
 */

import { Photo, SyncStatus } from "../types/Photo";

const twoDigits = (value: number): string => String(value).padStart(2, "0");

/** L'identifiant du produit qui s'ouvrirait à cet instant. */
export const productIdFor = (date: Date): string =>
  `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(
    date.getDate()
  )}/${twoDigits(date.getHours())}${twoDigits(date.getMinutes())}${twoDigits(
    date.getSeconds()
  )}`;

/**
 * Rattache à un produit les photos qui n'en portent pas encore.
 *
 * On ignore quels produits elles montrent : rien ne permet de reconstituer
 * après coup les salves. On respecte donc ce que le stockage distant faisait
 * déjà — un dossier par journée : chaque journée devient un produit, nommé par
 * l'heure de sa plus ancienne photo. Aucune photo n'en sort sans produit,
 * aucune n'en porte deux, et celles qui en ont déjà un ne sont pas touchées.
 */
export const assignProductIds = <
  T extends { createdAt: Date; productId?: string },
>(
  photos: T[]
): T[] => {
  const productByDay = new Map<string, string>();
  const chronological = [...photos].sort(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
  );

  for (const photo of chronological) {
    if (photo.productId) continue;
    const product = productIdFor(photo.createdAt);
    const day = product.slice(0, "AAAA-MM-JJ".length);
    if (!productByDay.has(day)) productByDay.set(day, product);
    photo.productId = productByDay.get(day);
  }

  return photos;
};

/** Le jour de capture d'un produit : le préfixe de son identifiant. */
export const productDay = (productId: string): string =>
  productId.slice(0, "AAAA-MM-JJ".length);

/**
 * L'heure de capture d'un produit, telle que la porte son identifiant.
 *
 * Les secondes restent : « Produit suivant » enchaîne les produits sans quitter
 * le viseur, si bien que deux d'entre eux se suivent parfois dans la même
 * minute — sans elles, deux lignes de l'accueil porteraient le même nom.
 */
export const productTime = (productId: string): string => {
  const time = productId.slice("AAAA-MM-JJ/".length);
  return `${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`;
};

/**
 * Ce que l'en-tête d'un jour annonce à droite : « à envoyer / total » tant que
 * quelque chose reste à partir, le total seul une fois la journée entière
 * arrivée. C'est ce qui permet de juger une journée sans la parcourir.
 */
export const dayCountLabel = (products: Product[]): string => {
  const unsent = products.filter((p) => p.status !== "sent").length;
  return unsent > 0 ? `${unsent} / ${products.length}` : `${products.length}`;
};

/**
 * Ce que l'appareil sait de l'envoi d'un produit : l'état le plus faible de ses
 * photos, dans cet ordre.
 *
 * Une photo envoyée reste sur l'appareil jusqu'à la purge — c'est son
 * `syncStatus` qui porte la preuve, et lui seul. Ne jamais reconduire
 * l'ancienne hypothèse selon laquelle « une photo encore là n'est pas partie » :
 * elle était vraie quand `syncPhoto` supprimait dans la foulée, elle est fausse
 * depuis. Le succès ne se déduit toujours pas de l'absence d'erreur : `SYNCED`
 * n'est écrit que sur les octets rendus par le distant. Mais le cas par défaut,
 * lui, cesse d'être un aveu d'ignorance — on n'y tombe que si chaque photo est
 * arrivée.
 */
export type ProductStatus = "failed" | "sending" | "pending" | "sent";

export const productStatus = (photos: Photo[]): ProductStatus => {
  if (photos.some((p) => p.syncStatus === SyncStatus.FAILED)) return "failed";
  if (photos.some((p) => p.syncStatus === SyncStatus.SYNCING)) return "sending";
  if (photos.some((p) => p.syncStatus !== SyncStatus.SYNCED)) return "pending";
  return "sent";
};

export interface Product {
  id: string;
  day: string;
  /** Les photos du produit restées sur l'appareil, la plus récente d'abord. */
  photos: Photo[];
  status: ProductStatus;
}

export interface ProductDay {
  day: string;
  products: Product[];
}

/**
 * Regroupe en produits les photos restées sur l'appareil, puis les produits
 * par jour de capture — ce que montre l'accueil.
 *
 * L'identifiant `AAAA-MM-JJ/HHMMSS` se trie comme le temps qu'il porte : le
 * plus récent vient en tête sans qu'aucune date n'ait à être relue, et son
 * préfixe donne le jour. Un produit vide ne peut pas apparaître, puisqu'il
 * n'est que le regroupement de photos existantes.
 */
export const groupProductsByDay = (photos: Photo[]): ProductDay[] => {
  const byProduct = new Map<string, Photo[]>();
  for (const photo of photos) {
    const group = byProduct.get(photo.productId);
    if (group) group.push(photo);
    else byProduct.set(photo.productId, [photo]);
  }

  const byDay = new Map<string, Product[]>();
  for (const id of [...byProduct.keys()].sort().reverse()) {
    const productPhotos = [...byProduct.get(id)!].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
    const day = productDay(id);
    const products = byDay.get(day) ?? [];
    products.push({
      id,
      day,
      photos: productPhotos,
      status: productStatus(productPhotos),
    });
    byDay.set(day, products);
  }

  return [...byDay].map(([day, products]) => ({ day, products }));
};

/** La fenêtre glissante que l'appareil garde : au-delà, un produit le quitte. */
export const RETENTION_DAYS = 7;

/**
 * Les produits que l'appareil peut oublier : envoi vérifié pour **toutes**
 * leurs photos, et capture vieille de plus de sept jours.
 *
 * La preuve prime sur l'âge. Un produit dont une seule photo n'est pas arrivée
 * reste, quel que soit son âge — la rétention n'est pas un mécanisme de perte,
 * et le stockage durable, lui, garde tout. `SYNCED` est le seul état qui vaut
 * preuve : `syncPhoto` ne l'écrit qu'après les octets rendus par le distant.
 *
 * C'est le seul chemin par lequel une photo envoyée quitte l'appareil, et il
 * emporte le produit entier d'un bloc : rien ne supprime plus au fil de l'eau.
 */
export const purgeableProducts = (
  photos: Photo[],
  now: Date = new Date()
): Product[] => {
  const limit = now.getTime() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
  return groupProductsByDay(photos)
    .flatMap((day) => day.products)
    .filter(
      (product) =>
        product.status === "sent" &&
        // `photos[0]` est la plus récente : un produit complété hier reste,
        // même si sa première photo date de trois semaines.
        product.photos[0].createdAt.getTime() < limit
    );
};
